import React from 'react';
import {
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { NavMenuItem } from './navConfig';
import { getChrome } from '../../theme/chrome';

interface NavItemProps {
  item: NavMenuItem;
  isSubItem?: boolean;
  pathname: string;
  themeMode: string;
  openGroups: Record<string, boolean>;
  onNavigate: (path: string) => void;
  onGroupToggle: (group: string) => void;
}

/**
 * One sidebar nav entry — a leaf link or an expandable group that recurses into
 * child <NavItem>s. Extracted verbatim from Layout.tsx's renderMenuItem /
 * renderRecursiveItems (advice #6, Layout slice 2). Layout passes the theme
 * mode, current pathname, open-group state, and navigate/toggle callbacks; the
 * themed sx is unchanged.
 */
const NavItem: React.FC<NavItemProps> = ({
  item, isSubItem = false, pathname, themeMode, openGroups, onNavigate, onGroupToggle,
}) => {
  const chrome = getChrome(themeMode);
  // Leaf item (has a path)
  if ('path' in item && item.path) {
    return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={pathname === item.path}
        onClick={() => onNavigate(item.path)}
        sx={{
          borderRadius: chrome.navRadius,
          mx: chrome.dark ? 1 : 0,
          pl: isSubItem ? 4 : 2,
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: themeMode === 'orange' ? `linear-gradient(
              90deg,
              transparent 0%,
              rgba(255, 255, 255, 0.2) 20%,
              rgba(255, 255, 255, 0.4) 40%,
              rgba(255, 255, 255, 0.6) 50%,
              rgba(255, 255, 255, 0.4) 60%,
              rgba(255, 255, 255, 0.2) 80%,
              transparent 100%
            )` : 'none',
            transform: 'translateX(-100%)',
            transition: 'none',
            pointerEvents: 'none',
            zIndex: 1,
            opacity: 0,
          },
          '&.Mui-selected': {
            backgroundColor: chrome.navSelectedBg,
            color: chrome.dark ? '#ffffff' : 'inherit',
            boxShadow: chrome.navSelectedShadow,
            '&:hover': {
              backgroundColor: chrome.navSelectedHoverBg,
              boxShadow: themeMode === 'orange' ? '0 8px 25px rgba(255, 140, 0, 0.5)' : 'none',
            },
            '& .MuiListItemIcon-root': {
              color: chrome.dark ? '#ffffff' : 'inherit',
              position: 'relative',
              zIndex: 2,
            },
            '& .MuiListItemText-root': {
              position: 'relative',
              zIndex: 2,
            }
          },
          '&:hover': {
            backgroundColor: chrome.navHover,
            boxShadow: themeMode === 'orange' ? '0 4px 15px rgba(255, 140, 0, 0.2)' : 'none',
            '&::before': {
              animation: themeMode === 'orange' ? 'wave 1.5s ease-in-out infinite' : 'none',
              opacity: themeMode === 'orange' ? 1 : 0,
            },
            '& .MuiListItemIcon-root': {
              color: themeMode === 'orange' ? '#ff8c00' : 'inherit',
              position: 'relative',
              zIndex: 2,
            },
            '& .MuiListItemText-root': {
              color: themeMode === 'orange' ? '#ff8c00' : 'inherit',
              position: 'relative',
              zIndex: 2,
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: chrome.navSelectedHoverBg,
            '& .MuiListItemIcon-root': {
              color: chrome.navSelectedHoverFg,
            },
            '& .MuiListItemText-root': {
              color: chrome.navSelectedHoverFg,
            },
          },
        }}
      >
        <ListItemIcon sx={{
          color: chrome.dark ? '#ffffff' : 'inherit',
          minWidth: 40,
          transition: 'color 0.2s ease-in-out'
        }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          sx={{
            color: chrome.dark ? '#ffffff' : 'inherit',
            transition: 'color 0.2s ease-in-out',
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
              fontWeight: pathname === item.path ? 600 : 400,
            }
          }}
        />
      </ListItemButton>
    </ListItem>
    );
  }

  // Grouped item (expands to child NavItems)
  if (item.group && item.items && item.items.length > 0) {
    return (
                  <React.Fragment>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => onGroupToggle(item.group)}
                        sx={{
                          borderRadius: chrome.navRadius,
                          mx: chrome.dark ? 1 : 0,
                          pl: isSubItem ? 4 : 2,
                          transition: 'background-color 200ms ease, color 200ms ease',
                          '&:hover': {
                            backgroundColor: themeMode === 'orange'
                              ? alpha('#ff8c00', 0.14)
                              : chrome.navHover,
                            '& .MuiListItemIcon-root, & .MuiListItemText-root, & .MuiTypography-root': {
                              color: themeMode === 'orange' ? '#ff8c00' : 'inherit',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{
                          color: chrome.dark ? '#ffffff' : 'inherit',
                          minWidth: 40
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          sx={{
                            color: chrome.dark ? '#ffffff' : 'inherit',
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                              fontWeight: 600,
                            }
                          }}
                        />
                        {openGroups[item.group] ?
                          <ExpandLess sx={{ color: chrome.dark ? '#ffffff' : 'inherit' }} /> :
                          <ExpandMore sx={{ color: chrome.dark ? '#ffffff' : 'inherit' }} />
                        }
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={openGroups[item.group]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.items.map((child) => (
                          <NavItem
                            key={child.path || child.group}
                            item={child}
                            isSubItem
                            pathname={pathname}
                            themeMode={themeMode}
                            openGroups={openGroups}
                            onNavigate={onNavigate}
                            onGroupToggle={onGroupToggle}
                          />
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
    );
  }

  return null;
};

export default NavItem;
